/**
 * Bound Shape Edit Interceptor
 * Intercepts edits to shapes with data bindings and routes them through
 * the commit-based workflow.
 *
 * When a shape has a data binding, property changes should be stored in
 * localChanges rather than directly updating the shape's base properties.
 * This enables users to review and commit/discard changes.
 *
 * SIBLING SYNC: When a shape is edited, all other shapes bound to the same
 * sourceId + recordId are automatically updated to reflect the change.
 */

import type * as Y from 'yjs';
import type { ShapeDataBinding } from '../models/ShapeDataBinding';
import { updateBoundProperty, updateBoundProperties } from '../utils/updateBoundShapeProperty';
import { updateBoundRecordChanges } from '../stores/BoundRecordStore';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../../lib/diagram/shapeGeometry';

/**
 * Shape info with binding
 */
interface ShapeWithBinding {
  shapeId: string;
  shape: Record<string, unknown>;
  binding: ShapeDataBinding;
}

export interface BoundEditResult {
  intercepted: boolean;
  success: boolean;
  updatedBinding?: ShapeDataBinding;
  error?: string;
}

/**
 * Field mapping for shape properties to data binding fields.
 * Maps the shape property names to the corresponding data source column names.
 */
export interface FieldMappingConfig {
  nameColumn?: string;
  descriptionColumn?: string;
  statusColumn?: string;
  ownerColumn?: string;
  // Additional custom mappings can be added
  [key: string]: string | undefined;
}

/**
 * Default field mapping - maps standard shape properties to common column names
 */
const DEFAULT_FIELD_MAPPING: FieldMappingConfig = {
  name: 'Name',
  text: 'Name',
  description: 'Description',
  status: 'Status',
  owner: 'Owner',
};

/**
 * Check if a shape has a data binding
 * Checks both top-level shape.dataBinding and props.dataBinding
 */
export function hasDataBinding(shape: Record<string, unknown>): boolean {
  // Check top-level first
  const topLevelBinding = shape.dataBinding as ShapeDataBinding | undefined;
  if (topLevelBinding?.sourceId !== undefined) {
    return true;
  }

  // Fall back to props.dataBinding
  const props = shape.props as Record<string, unknown> | undefined;
  const propsBinding = props?.dataBinding as ShapeDataBinding | undefined;
  if (propsBinding?.sourceId !== undefined) {
    return true;
  }

  const attrs = shape.attrs as Record<string, unknown> | undefined;
  const attrsBinding = attrs?.dataBinding as ShapeDataBinding | undefined;
  return attrsBinding !== undefined && attrsBinding.sourceId !== undefined;
}

/**
 * Get the data binding from a shape
 * Checks both top-level shape.dataBinding and props.dataBinding
 * (different code paths may store the binding in different locations)
 */
export function getDataBinding(shape: Record<string, unknown>): ShapeDataBinding | null {
  // First check top-level (where BindShapeToRecordCommand stores it)
  const topLevelBinding = shape.dataBinding as ShapeDataBinding | undefined;
  if (topLevelBinding?.sourceId) {
    return topLevelBinding;
  }

  // Fall back to props.dataBinding (alternative storage location)
  const props = shape.props as Record<string, unknown> | undefined;
  const propsBinding = props?.dataBinding as ShapeDataBinding | undefined;
  if (propsBinding?.sourceId) {
    return propsBinding;
  }

  const attrs = shape.attrs as Record<string, unknown> | undefined;
  const attrsBinding = attrs?.dataBinding as ShapeDataBinding | undefined;
  return attrsBinding ?? null;
}

/**
 * Find all sibling shapes bound to the same sourceId + recordId
 * (excluding the source shape itself)
 */
export function findSiblingBoundShapes(
  doc: Y.Doc,
  sourceId: string,
  recordId: string,
  excludeShapeId: string
): ShapeWithBinding[] {
  const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const siblings: ShapeWithBinding[] = [];

  shapesMap.forEach((rawShape, shapeId) => {
    if (shapeId === excludeShapeId) return;

    const shape = readYjsValue<Record<string, unknown>>(rawShape);
    const binding = getDataBinding(shape);
    if (binding && binding.sourceId === sourceId && binding.recordId === recordId) {
      siblings.push({
        shapeId,
        shape,
        binding,
      });
    }
  });

  return siblings;
}

/**
 * Sync a property change to all sibling shapes bound to the same record.
 * Updates both the dataBinding.localChanges and the visual property.
 */
function syncToSiblingShapes(
  doc: Y.Doc,
  sourceShapeId: string,
  binding: ShapeDataBinding,
  property: string,
  value: unknown,
  userId: string,
  fieldMapping?: FieldMappingConfig
): void {
  const siblings = findSiblingBoundShapes(
    doc,
    binding.sourceId,
    binding.recordId,
    sourceShapeId
  );

  if (siblings.length === 0) return;

  const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const mapping = fieldMapping ?? DEFAULT_FIELD_MAPPING;
  const dataSourceField = mapping[property] ?? property;

  // Update all siblings within the same transaction
  for (const sibling of siblings) {
    // Update the sibling's localChanges to match
    const siblingUpdatedBinding = updateBoundProperty(
      sibling.binding,
      dataSourceField,
      value,
      userId
    );

    const siblingProps = (sibling.shape.props as Record<string, unknown>) ?? {};
    const siblingAttrs = (sibling.shape.attrs as Record<string, unknown>) ?? {};
    shapesMap.patch(sibling.shapeId, {
      ...sibling.shape,
      props: {
        ...siblingProps,
        dataBinding: siblingUpdatedBinding.updatedBinding,
      },
      attrs: siblingAttrs.dataBinding
        ? { ...siblingAttrs, dataBinding: siblingUpdatedBinding.updatedBinding }
        : siblingAttrs,
      // Also update the shape's visual property
      [property]: value,
    });
  }
}

/**
 * Sync multiple property changes to all sibling shapes bound to the same record.
 */
function syncPropertiesToSiblingShapes(
  doc: Y.Doc,
  sourceShapeId: string,
  binding: ShapeDataBinding,
  updates: Record<string, unknown>,
  userId: string,
  fieldMapping?: FieldMappingConfig
): void {
  const siblings = findSiblingBoundShapes(
    doc,
    binding.sourceId,
    binding.recordId,
    sourceShapeId
  );

  if (siblings.length === 0) return;

  const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const mapping = fieldMapping ?? DEFAULT_FIELD_MAPPING;

  // Map shape properties to data source fields
  const dataUpdates: Record<string, unknown> = {};
  for (const [property, value] of Object.entries(updates)) {
    const dataSourceField = mapping[property] ?? property;
    dataUpdates[dataSourceField] = value;
  }

  // Update all siblings
  for (const sibling of siblings) {
    const siblingUpdatedBinding = updateBoundProperties(
      sibling.binding,
      dataUpdates,
      userId
    );

    const siblingProps = (sibling.shape.props as Record<string, unknown>) ?? {};
    const siblingAttrs = (sibling.shape.attrs as Record<string, unknown>) ?? {};
    shapesMap.patch(sibling.shapeId, {
      ...sibling.shape,
      ...updates, // Apply all visual updates
      props: {
        ...siblingProps,
        dataBinding: siblingUpdatedBinding,
      },
      attrs: siblingAttrs.dataBinding
        ? { ...siblingAttrs, dataBinding: siblingUpdatedBinding }
        : siblingAttrs,
    });
  }
}

/**
 * Intercept a single property edit on a shape
 *
 * @param doc - The Yjs document
 * @param shapeId - The shape ID being edited
 * @param property - The property being changed
 * @param value - The new value
 * @param userId - The ID of the user making the change
 * @param fieldMapping - Optional mapping from shape properties to data source columns
 * @returns Result indicating if the edit was intercepted and handled
 */
export function interceptPropertyEdit(
  doc: Y.Doc,
  shapeId: string,
  property: string,
  value: unknown,
  userId: string,
  fieldMapping?: FieldMappingConfig
): BoundEditResult {
  const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const rawShape = shapesMap.get(shapeId);
  const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;

  if (!shape) {
    return { intercepted: false, success: false, error: 'Shape not found' };
  }

  const binding = getDataBinding(shape);

  if (!binding) {
    // No binding - let the normal edit flow handle this
    return { intercepted: false, success: true };
  }

  // Map the shape property to the data source field
  const mapping = fieldMapping ?? DEFAULT_FIELD_MAPPING;
  const dataSourceField = mapping[property] ?? property;

  // If this property isn't mapped to a data field, don't intercept
  // (allow style changes, position changes, etc. to go through normally)
  const dataRelatedProperties = ['name', 'text', 'description', 'status', 'owner', ...Object.keys(mapping)];
  if (!dataRelatedProperties.includes(property.toLowerCase())) {
    return { intercepted: false, success: true };
  }

  // Update the binding's localChanges
  const result = updateBoundProperty(binding, dataSourceField, value, userId);

  if (!result.success) {
    return { intercepted: true, success: false, error: 'Failed to update bound property' };
  }

  // Keep centralized record state in sync for reactive repository/UI updates.
  updateBoundRecordChanges(
    doc,
    binding.sourceId,
    binding.recordId,
    { [dataSourceField]: value },
    binding.cachedRecord,
    userId
  );

  // Write the updated binding back to Yjs and sync sibling shapes
  doc.transact(() => {
    const props = (shape.props as Record<string, unknown>) ?? {};
    const attrs = (shape.attrs as Record<string, unknown>) ?? {};
    shapesMap.patch(shapeId, {
      ...shape,
      props: {
        ...props,
        dataBinding: result.updatedBinding,
      },
      attrs: attrs.dataBinding ? { ...attrs, dataBinding: result.updatedBinding } : attrs,
      // Also update the shape's visual property so the UI reflects the change
      [property]: value,
    });

    // Sync to all sibling shapes bound to the same record
    syncToSiblingShapes(
      doc,
      shapeId,
      binding,
      property,
      value,
      userId,
      fieldMapping
    );
  }, 'bound-edit');

  return {
    intercepted: true,
    success: true,
    updatedBinding: result.updatedBinding,
  };
}

/**
 * Intercept multiple property edits on a shape
 *
 * @param doc - The Yjs document
 * @param shapeId - The shape ID being edited
 * @param updates - Object containing property names and new values
 * @param userId - The ID of the user making the change
 * @param fieldMapping - Optional mapping from shape properties to data source columns
 * @returns Result indicating if the edit was intercepted and handled
 */
export function interceptPropertiesEdit(
  doc: Y.Doc,
  shapeId: string,
  updates: Record<string, unknown>,
  userId: string,
  fieldMapping?: FieldMappingConfig
): BoundEditResult {
  const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const rawShape = shapesMap.get(shapeId);
  const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;

  if (!shape) {
    return { intercepted: false, success: false, error: 'Shape not found' };
  }

  const binding = getDataBinding(shape);

  if (!binding) {
    // No binding - let the normal edit flow handle this
    return { intercepted: false, success: true };
  }

  // Map shape properties to data source fields
  const mapping = fieldMapping ?? DEFAULT_FIELD_MAPPING;
  const dataUpdates: Record<string, unknown> = {};
  const nonDataUpdates: Record<string, unknown> = {};

  const dataRelatedProperties = new Set([
    'name',
    'text',
    'description',
    'status',
    'owner',
    ...Object.keys(mapping).map((k) => k.toLowerCase()),
  ]);

  for (const [property, value] of Object.entries(updates)) {
    if (dataRelatedProperties.has(property.toLowerCase())) {
      const dataSourceField = mapping[property] ?? property;
      dataUpdates[dataSourceField] = value;
    } else {
      nonDataUpdates[property] = value;
    }
  }

  // If no data-related updates, don't intercept
  if (Object.keys(dataUpdates).length === 0) {
    return { intercepted: false, success: true };
  }

  // Update the binding's localChanges with data-related updates
  const updatedBinding = updateBoundProperties(binding, dataUpdates, userId);

  // Keep centralized record state in sync for reactive repository/UI updates.
  updateBoundRecordChanges(
    doc,
    binding.sourceId,
    binding.recordId,
    dataUpdates,
    binding.cachedRecord,
    userId
  );

  // Write the updated binding and all updates back to Yjs
  doc.transact(() => {
    const props = (shape.props as Record<string, unknown>) ?? {};
    const attrs = (shape.attrs as Record<string, unknown>) ?? {};
    shapesMap.patch(shapeId, {
      ...shape,
      ...updates, // Apply all updates to the shape (both data and non-data)
      props: {
        ...props,
        dataBinding: updatedBinding,
      },
      attrs: attrs.dataBinding ? { ...attrs, dataBinding: updatedBinding } : attrs,
    });

    // Sync to all sibling shapes bound to the same record
    // Only sync the data-related updates, not style/position changes
    const dataRelatedUpdates: Record<string, unknown> = {};
    for (const [property, value] of Object.entries(updates)) {
      if (dataRelatedProperties.has(property.toLowerCase())) {
        dataRelatedUpdates[property] = value;
      }
    }
    if (Object.keys(dataRelatedUpdates).length > 0) {
      syncPropertiesToSiblingShapes(
        doc,
        shapeId,
        binding,
        dataRelatedUpdates,
        userId,
        fieldMapping
      );
    }
  }, 'bound-edit');

  return {
    intercepted: true,
    success: true,
    updatedBinding,
  };
}

/**
 * Create a wrapped update function that intercepts bound shape edits
 *
 * @param doc - The Yjs document
 * @param userId - The ID of the user making changes
 * @param fieldMapping - Optional mapping from shape properties to data source columns
 * @returns A function that can be used in place of direct Yjs updates
 */
export function createBoundEditInterceptor(
  doc: Y.Doc,
  userId: string,
  fieldMapping?: FieldMappingConfig
) {
  return {
    /**
     * Update a single property on a shape, intercepting if bound
     */
    updateProperty: (shapeId: string, property: string, value: unknown): BoundEditResult => {
      return interceptPropertyEdit(doc, shapeId, property, value, userId, fieldMapping);
    },

    /**
     * Update multiple properties on a shape, intercepting if bound
     */
    updateProperties: (shapeId: string, updates: Record<string, unknown>): BoundEditResult => {
      return interceptPropertiesEdit(doc, shapeId, updates, userId, fieldMapping);
    },

    /**
     * Check if a shape is bound
     */
    isBound: (shapeId: string): boolean => {
      const shapesMap = new ShapeMapAdapter(doc.getMap('shapes'));
      const raw = shapesMap.get(shapeId);
      const shape = raw != null ? readYjsValue<Record<string, unknown>>(raw) : undefined;
      return shape ? hasDataBinding(shape) : false;
    },

    /**
     * Get the binding for a shape
     */
    getBinding: (shapeId: string): ShapeDataBinding | null => {
      const shapesMap = new ShapeMapAdapter(doc.getMap('shapes'));
      const raw = shapesMap.get(shapeId);
      const shape = raw != null ? readYjsValue<Record<string, unknown>>(raw) : undefined;
      return shape ? getDataBinding(shape) : null;
    },
  };
}
